/* ==========================================================================
   VYORO B2B — Category navigation
   Mega menu (desktop) and category drill-down (mobile drawer).

   Structure is rendered by Liquid from a Shopify menu; this module only
   handles opening, closing, focus and keyboard behaviour.
   ========================================================================== */

(function () {
  'use strict';

  const VYORO = (window.VYORO = window.VYORO || {});
  const reduced = () =>
    (VYORO.utils && VYORO.utils.prefersReducedMotion && VYORO.utils.prefersReducedMotion()) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Mega menu
     ---------------------------------------------------------------------- */

  function initMegaMenu() {
    const nav = document.querySelector('[data-category-nav]');
    if (!nav) return;

    const panel = nav.querySelector('[data-mega-menu]');
    const triggers = Array.from(nav.querySelectorAll('[data-mega-trigger]'));
    if (!panel || !triggers.length) return;

    let isOpen = false;
    let hoverTimer = null;

    const setExpanded = (state) =>
      triggers.forEach((t) => t.setAttribute('aria-expanded', String(state)));

    function open() {
      if (isOpen) return;
      isOpen = true;
      panel.hidden = false;

      // Two frames so the transition runs from the collapsed state.
      if (reduced()) {
        panel.classList.add('is-open');
      } else {
        // Re-check state inside the frame: a close can land between the call
        // and the callback, which would otherwise leave is-open on a hidden
        // panel and make the next open skip its transition.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (isOpen) panel.classList.add('is-open');
          });
        });
      }

      setExpanded(true);
    }

    function close(options) {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      setExpanded(false);

      const finish = () => {
        if (!isOpen) panel.hidden = true;
      };

      if (reduced()) finish();
      else setTimeout(finish, 240);

      if (options && options.restoreFocus && triggers[0]) triggers[0].focus();
    }

    function toggle(trigger) {
      isOpen ? close() : open();
      if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        toggle(trigger);
      });
    });

    // Pointer affordance: open on hover, but only for real pointers, and
    // always with a small delay so a passing cursor does not flash the panel.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const scheduleOpen = () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(open, 120);
      };
      const scheduleClose = () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          if (!nav.matches(':hover')) close();
        }, 200);
      };

      triggers.forEach((t) => t.addEventListener('mouseenter', scheduleOpen));
      panel.addEventListener('mouseenter', () => clearTimeout(hoverTimer));
      nav.addEventListener('mouseleave', scheduleClose);
    }

    // Escape closes and returns focus to the trigger.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.stopPropagation();
        close({ restoreFocus: true });
      }
    });

    // Clicking outside closes.
    document.addEventListener('click', (event) => {
      if (isOpen && !nav.contains(event.target)) close();
    });

    // Tabbing out of the navigation closes it, so keyboard users are never
    // left with an invisible open panel behind them.
    nav.addEventListener('focusout', (event) => {
      if (isOpen && !nav.contains(event.relatedTarget)) close();
    });
  }

  /* ------------------------------------------------------------------------
     Mobile drill-down
     ---------------------------------------------------------------------- */

  function initDrilldown() {
    const body = document.querySelector('[data-mobile-nav-body]');
    if (!body) return;

    const root = body.querySelector('[data-drill-root]');
    const panels = Array.from(body.querySelectorAll('[data-drill-panel]'));
    if (!root || !panels.length) return;

    function openPanel(panel) {
      panel.hidden = false;

      if (reduced()) {
        panel.classList.add('is-open');
      } else {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (!panel.hidden) panel.classList.add('is-open');
          });
        });
      }

      // Move focus to the panel's back button so keyboard and screen-reader
      // users land inside the level they just opened.
      const back = panel.querySelector('[data-drill-back]');
      if (back) window.requestAnimationFrame(() => back.focus());
    }

    function closePanel(panel, returnFocusTo) {
      panel.classList.remove('is-open');

      const finish = () => {
        panel.hidden = true;
      };

      if (reduced()) finish();
      else setTimeout(finish, 240);

      if (returnFocusTo) returnFocusTo.focus();
    }

    function closeAll() {
      panels.forEach((p) => {
        p.classList.remove('is-open');
        p.hidden = true;
      });
    }

    body.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drill-open]');
      if (opener) {
        event.preventDefault();
        const panel = document.getElementById(opener.getAttribute('data-drill-open'));
        if (panel) {
          panel.__opener = opener;
          openPanel(panel);
        }
        return;
      }

      const back = event.target.closest('[data-drill-back]');
      if (back) {
        event.preventDefault();
        const panel = back.closest('[data-drill-panel]');
        if (panel) closePanel(panel, panel.__opener);
      }
    });

    // Escape steps back one level rather than closing the whole drawer.
    body.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openPanelEl = panels.find((p) => p.classList.contains('is-open'));
      if (openPanelEl) {
        event.stopPropagation();
        closePanel(openPanelEl, openPanelEl.__opener);
      }
    });

    // Reset to the top level whenever the drawer is reopened.
    const drawer = document.getElementById('MobileNav');
    if (drawer) drawer.addEventListener('overlay:close', closeAll);
  }

  function init() {
    initMegaMenu();
    initDrilldown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
